import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { useWorkspaceOrg } from "@/contexts/WorkspaceOrgContext";
import { formatReceiptPayload } from "@/lib/ops/inboundReceiptsModel";
import { requestWorkspaceMode } from "@/lib/workspaceModeNavigation";
import { openOpsPaperworkDeal } from "@/lib/opsLookup/openOpsPaperworkDeal";
import type { InboundReceiptDetail, InboundReceiptRow } from "@/lib/inboundReceiptsClient";

const btnClass =
  "rounded border border-gray-300 px-2 py-0.5 text-[11px] text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-800";

function statusClass(status: string): string {
  if (status === "error") return "text-rose-700 dark:text-rose-300";
  if (status === "skipped_duplicate") return "text-amber-700 dark:text-amber-300";
  return "text-emerald-700 dark:text-emerald-300";
}

function CopyJsonButton(props: { text: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    if (!props.text) return;
    try {
      await navigator.clipboard.writeText(props.text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      /* ignore */
    }
  }
  return (
    <button
      type="button"
      className={`${btnClass} inline-flex items-center gap-1 ${
        copied ? "border-emerald-500 bg-emerald-50 text-emerald-800 dark:border-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-200" : ""
      }`}
      onClick={() => void copy()}
    >
      {copied ? <Check className="h-3 w-3" aria-hidden /> : <Copy className="h-3 w-3" aria-hidden />}
      {copied ? "Copied" : "Copy JSON"}
    </button>
  );
}

export function DevInboundReceiptsRow(props: {
  row: InboundReceiptRow;
  expanded: boolean;
  payloadBusy: boolean;
  detail: InboundReceiptDetail | undefined;
  onToggle: () => void;
}) {
  const { setWorkspaceOrganizationId } = useWorkspaceOrg();
  const { row, detail } = props;
  const json = detail ? formatReceiptPayload(detail.payload) : "";
  const unlinked = !row.deal_id && !row.intake_id;

  function openDeal() {
    if (!row.deal_id) return;
    setWorkspaceOrganizationId(row.organization_id);
    openOpsPaperworkDeal(row.deal_id);
  }

  function openIntake() {
    if (!row.intake_id) return;
    setWorkspaceOrganizationId(row.organization_id);
    void navigator.clipboard.writeText(row.intake_id).catch(() => undefined);
    requestWorkspaceMode({ mode: "salesIntake" });
  }

  return (
    <li className="rounded-lg border border-gray-200 px-3 py-2 dark:border-zinc-700">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
        <span className="font-mono text-xs text-gray-800 dark:text-zinc-100">{row.source}</span>
        <span className="font-mono text-xs text-gray-500">{row.event_type}</span>
        <span className={`font-mono text-xs ${statusClass(row.processing_status)}`}>{row.processing_status}</span>
        <span className="text-xs text-gray-500">{row.created_at ? new Date(row.created_at).toLocaleString() : ""}</span>
        {unlinked ? <span className="text-[11px] text-amber-800 dark:text-amber-200">unlinked</span> : null}
      </div>
      {row.error_message ? (
        <p className="mt-1 text-[11px] text-rose-700 dark:text-rose-300">{row.error_message}</p>
      ) : null}
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {row.deal_id ? (
          <button type="button" className={btnClass} onClick={openDeal}>
            Open deal {row.deal_id.slice(0, 8)}…
          </button>
        ) : null}
        {row.intake_id ? (
          <button type="button" className={btnClass} title="Copies intake id and opens Order Intake" onClick={openIntake}>
            Open intake {row.intake_id.slice(0, 8)}…
          </button>
        ) : null}
        <button type="button" className={btnClass} onClick={props.onToggle}>
          {props.expanded ? "Hide JSON" : "Show JSON"}
        </button>
      </div>
      {props.expanded ? (
        <div className="mt-2 space-y-1">
          {props.payloadBusy && !detail ? (
            <p className="text-[11px] text-gray-500">Loading payload…</p>
          ) : (
            <>
              <div className="flex justify-end">
                <CopyJsonButton text={json} />
              </div>
              <pre className="max-h-80 overflow-auto rounded-md bg-gray-50 p-2 text-[11px] leading-snug dark:bg-zinc-950">
                {json || "(empty payload)"}
              </pre>
            </>
          )}
        </div>
      ) : null}
    </li>
  );
}
